import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { CustomGridColDef } from '../services';
import { columnRenderer } from '../services';
import type { SavedColDef } from '../store';
import { addPaddingColumns, type PaddingColumnsConfig } from './addPaddingColumns';
import { isPaddingColumn, isSystemColumn } from './columnFilters';
import { createSkeletonRenderer } from './createSkeletonRenderer';
import { mergeColumnsWithStore } from './mergeColumnsWithStore';
import { processColumns } from './processColumns';

export type ColumnPipelineConfig<T extends GridValidRowModel> = {
  storeColumns: SavedColDef[] | null;
  realDefaultColumns: CustomGridColDef<T>[];
  systemColumns: CustomGridColDef<T>[];
  defaultColumns: CustomGridColDef<T>[];
  cannotBeHiddenDefaults: CustomGridColDef<T>[];
  withSkeletonSupport: boolean;
  paddingConfig?: PaddingColumnsConfig;
};

export type ColumnPipelineResult<T extends GridValidRowModel> = {
  visibleColumns: (GridColDef<T> | CustomGridColDef<T>)[];
  allColumns: (GridColDef<T> | CustomGridColDef<T>)[];
  pinnedColumns: string[];
  defaultSort: string | undefined;
  columnsForFilters: GridColDef<T>[];
};

const filterAndMergeColumns = <T extends GridValidRowModel>(
  storeColumns: SavedColDef[] | null,
  realDefaultColumns: CustomGridColDef<T>[],
): CustomGridColDef<T>[] => {
  if (!storeColumns || storeColumns.length === 0) {
    return realDefaultColumns;
  }

  const realStoreColumns = storeColumns.filter((col) => !isPaddingColumn(col) && !isSystemColumn(col));
  return mergeColumnsWithStore(realStoreColumns, realDefaultColumns);
};

const addSystemColumns = <T extends GridValidRowModel>(
  columns: CustomGridColDef<T>[],
  systemColumns: CustomGridColDef<T>[],
): CustomGridColDef<T>[] => [...columns, ...systemColumns];

const applyRenderersAndSkeletons = <T extends GridValidRowModel>(
  columns: CustomGridColDef<T>[],
  withSkeletonSupport: boolean,
): GridColDef<T>[] => {
  const renderedColumns = columnRenderer<T>(columns);

  if (!withSkeletonSupport) {
    return renderedColumns;
  }

  return renderedColumns.map((col) => {
    return {
      ...col,
      renderCell: createSkeletonRenderer(col),
    };
  });
};

const addPaddingAndCannotBeHidden = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  cannotBeHiddenDefaults: CustomGridColDef<T>[],
  paddingConfig?: PaddingColumnsConfig,
): {
  columns: (GridColDef<T> | CustomGridColDef<T>)[];
  leftColumnName: string;
} => {
  const { columns: columnsWithPadding, leftColumnName } = addPaddingColumns(columns, paddingConfig);

  const paddingFieldsSet = new Set(columnsWithPadding.map((col) => col.field));
  const missingCannotBeHidden = cannotBeHiddenDefaults.filter((col) => !paddingFieldsSet.has(col.field));
  const renderedMissingCannotBeHidden = columnRenderer(missingCannotBeHidden);

  const allColumns = [...renderedMissingCannotBeHidden, ...columnsWithPadding];

  return { columns: allColumns, leftColumnName };
};

export const buildColumnPipeline = <T extends GridValidRowModel>(
  config: ColumnPipelineConfig<T>,
): ColumnPipelineResult<T> => {
  const {
    storeColumns,
    realDefaultColumns,
    systemColumns,
    defaultColumns,
    cannotBeHiddenDefaults,
    withSkeletonSupport,
    paddingConfig,
  } = config;

  const mergedColumns = filterAndMergeColumns(storeColumns, realDefaultColumns);

  const columnsWithSystem = addSystemColumns(mergedColumns, systemColumns);

  const columnsWithSkeletons = applyRenderersAndSkeletons(columnsWithSystem, withSkeletonSupport);

  const { columns: allColumns, leftColumnName } = addPaddingAndCannotBeHidden(
    columnsWithSkeletons,
    cannotBeHiddenDefaults,
    paddingConfig,
  );

  const processed = processColumns(allColumns, defaultColumns, leftColumnName);

  return {
    visibleColumns: processed.visible,
    allColumns,
    pinnedColumns: processed.pinned,
    defaultSort: processed.defaultSort,
    columnsForFilters: columnsWithSkeletons,
  };
};
